import { inject, Injectable } from '@angular/core';
import { HomesService } from '@shared/services/homes.service';
import { environment } from '../../../environments/environment.dev';
import { JobsList } from '@shared/types/jobs-list.type';
import { Observable, of, shareReplay, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SSEHandler {
  private readonly http = inject(HttpClient);
  private readonly homesService = inject(HomesService);
  private readonly BASE_URL = `${environment.baseUrl}`;
  private _eventSource: EventSource | undefined;
  private message = new Subject<JobsList>();
  private error = new Subject<Event>();

  private set eventSource(eventSource: EventSource | undefined) {
    this._eventSource = eventSource;
  }

  private get eventSource(): EventSource | undefined {
    return this._eventSource;
  }

  public get onMessage(): Observable<JobsList> {
    return this.message.asObservable().pipe(shareReplay(1));
  }

  public get onError(): Observable<Event> {
    return this.error.asObservable().pipe(shareReplay(1));
  }

  public init(): void {
    const entity = this.homesService.currentVisitedHome$.value.name;

    if (!entity) {
      throw new Error("Couldn't set entity for SSE handler");
    }

    if (this.eventSource) {
      console.info('SSE handler already initialized for entity');
      return;
    }

    const token = sessionStorage.getItem('access_token');
    const param = `?token=${token}`;
    this.eventSource = new EventSource(
      `${this.BASE_URL}/${entity}/jobs/sse${param}`,
    );

    this.initSSEMessagesSub();
  }

  public destroy(): void {
    if (!this.eventSource) return;

    this.eventSource.close();
    this.eventSource = undefined;
  }

  public cancelJob(id: number): Observable<unknown> {
    if (!id) return of();

    const endpoint = `${this.BASE_URL}/${this.homesService.currentVisitedHome$.value.name}/jobs/cancel`;
    return this.http.post(endpoint, { id }).pipe();
  }

  public downloadJobPreview(id: number): Observable<Blob> {
    return this.http.get(
      `${this.BASE_URL}/${this.homesService.currentVisitedHome$.value?.name}/jobs/${id}/download`,
      { responseType: 'blob' },
    );
  }

  private initSSEMessagesSub(): void {
    if (!this.eventSource) return;

    this.initOnMessageSub();
    this.initOnErrorSub();
  }

  private initOnMessageSub(): void {
    if (!this.eventSource) return;

    this.eventSource.onmessage = (event: MessageEvent<string>) => {
      const fixed = event.data.replace(/'/g, '"');
      const parsed = JSON.parse(fixed);
      this.message.next(parsed);
    };
  }

  private initOnErrorSub(): void {
    if (!this.eventSource) return;

    this.eventSource.onerror = (event: Event) => {
      console.error(event);
      this.error.next(event);
    };
  }
}
